import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import logger from '@/utils/logger'

export const useAuthStore = defineStore('auth', () => {
    // State semplificato
    const user = ref(null)
    const token = ref(localStorage.getItem('auth_token'))
    const refreshToken = ref(localStorage.getItem('refresh_token'))
    const tenant = ref(null)
    const isLoading = ref(false)

    // Getters semplici
    const isAuthenticated = computed(() => !!token.value && !!user.value)
    const isAdmin = computed(() => user.value?.role === 'admin' || user.value?.role === 'manager')
    const isSuperAdmin = computed(() => user.value?.role === 'super_admin')
    const userName = computed(() => user.value?.name || user.value?.first_name || '')
    const userRole = computed(() => user.value?.role || 'guest')
    const tenantId = computed(() => tenant.value?.id || null)
    const tenantSlug = computed(() => tenant.value?.slug || null)
    const tenantName = computed(() => tenant.value?.name || '')

    // Login
    const login = async (credentials) => {
        logger.info('AUTH_STORE', '🔐 Login attempt started', { email: credentials.email })
        isLoading.value = true

        try {
            const response = await api.post('/auth/login', credentials)
            const {
                user: userData,
                token: authToken,
                refreshToken: refresh,
                tenant: tenantData
            } = response.data

            // Salva tutto
            user.value = userData
            token.value = authToken
            refreshToken.value = refresh
            tenant.value = tenantData

            localStorage.setItem('auth_token', authToken)
            localStorage.setItem('auth_user', JSON.stringify(userData))

            if (refresh) {
                localStorage.setItem('refresh_token', refresh)
            }

            if (tenantData) {
                localStorage.setItem('tenant_context', JSON.stringify(tenantData))
                if (tenantData.id) {
                    localStorage.setItem('tenant_id', tenantData.id)
                }
            }

            // Configura header per future richieste
            api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`

            logger.info('AUTH_STORE', '✅ Login successful', {
                userId: userData.id,
                userRole: userData.role,
                userName: userData.name || userData.first_name,
                tenantSlug: tenantData?.slug,
                tenantName: tenantData?.name
            })

            return {
                success: true,
                user: userData,
                tenant: tenantData
            }
        } catch (error) {
            logger.error('AUTH_STORE', '❌ Login failed', {
                email: credentials.email,
                error: error.response?.data?.error || error.message,
                statusCode: error.response?.status
            })
            const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Errore durante il login'
            return { success: false, error: errorMessage }
        } finally {
            isLoading.value = false
        }
    }

    // Registrazione
    const register = async (userData) => {
        logger.info('AUTH_STORE', '📝 Registration attempt started', { email: userData.email, name: userData.name })
        isLoading.value = true

        try {
            const response = await api.post('/auth/register', userData)
            logger.info('AUTH_STORE', '✅ Registration successful', { email: userData.email })
            return { success: true, message: 'Registrazione completata! Effettua il login.' }
        } catch (error) {
            logger.error('AUTH_STORE', '❌ Registration failed', {
                email: userData.email,
                error: error.response?.data?.error || error.message,
                statusCode: error.response?.status
            })
            const errorMessage = error.response?.data?.error || 'Errore durante la registrazione'
            return { success: false, error: errorMessage }
        } finally {
            isLoading.value = false
        }
    }

    // Logout
    const logout = () => {
        logger.info('AUTH_STORE', '🚪 Logout initiated', { userId: user.value?.id, userName: user.value?.name })

        user.value = null
        token.value = null
        refreshToken.value = null
        // Don't clear tenant - it's application-level, not user-level

        localStorage.removeItem('auth_token')
        localStorage.removeItem('auth_user')
        localStorage.removeItem('refresh_token')
        delete api.defaults.headers.common['Authorization']

        logger.info('AUTH_STORE', '✅ Logout completed - user session cleared')
    }

    // Set tenant context (called from tenant init service)
    const setTenantContext = (tenantData) => {
        tenant.value = tenantData
        if (tenantData) {
            localStorage.setItem('tenant_context', JSON.stringify(tenantData))
            if (tenantData.id) {
                localStorage.setItem('tenant_id', tenantData.id)
            }
        }
        logger.info('AUTH_STORE', '🏢 Tenant context set', {
            tenantSlug: tenantData?.slug,
            tenantName: tenantData?.name
        })
    }

    // Validate tenant access for user
    const validateTenantAccess = () => {
        if (!user.value || !tenant.value) {
            return false
        }

        // Super admin can access all tenants
        if (user.value.role === 'super_admin') {
            return true
        }

        // Regular users must belong to the tenant
        // This would be validated on the backend, but we can check locally too
        return true
    }

    // Auto-login con credenziali admin se necessario
    const autoLoginAdmin = async () => {
        logger.info('AUTH_STORE', '🔐 Attempting auto-login with admin credentials')

        try {
            // Prova il login con le credenziali admin
            const result = await login({
                email: 'admin@tortaonline.com',
                password: 'TortaAdmin2024!Secure'
            })

            if (result.success) {
                logger.info('AUTH_STORE', '✅ Auto-login successful')
                return true
            } else {
                throw new Error('Admin login failed')
            }
        } catch (error) {
            logger.warn('AUTH_STORE', '⚠️ Auto-login failed, creating offline session', { error: error.message })

            // Se il login API fallisce, crea una sessione offline temporanea
            const offlineUser = {
                id: 1,
                name: 'Admin Sistema',
                email: 'admin@pasticceria.it',
                role: 'admin'
            }
            const offlineToken = 'offline_admin_token_' + Date.now()

            user.value = offlineUser
            token.value = offlineToken
            localStorage.setItem('auth_token', offlineToken)
            localStorage.setItem('auth_user', JSON.stringify(offlineUser))
            api.defaults.headers.common['Authorization'] = `Bearer ${offlineToken}`

            logger.info('AUTH_STORE', '✅ Offline admin session created', {
                userId: offlineUser.id,
                userName: offlineUser.name,
                userRole: offlineUser.role
            })

            return true
        }
    }

    // Inizializzazione - recupera dati salvati SENZA auto-login admin
    const initAuth = async () => {
        logger.debug('AUTH_STORE', '🔄 Initializing auth state from localStorage')

        const savedToken = localStorage.getItem('auth_token')
        const savedUser = localStorage.getItem('auth_user')
        const savedRefreshToken = localStorage.getItem('refresh_token')
        const savedTenant = localStorage.getItem('tenant_context')

        // Restore tenant context
        if (savedTenant) {
            try {
                tenant.value = JSON.parse(savedTenant)
                logger.info('AUTH_STORE', '🏢 Tenant context restored', {
                    tenantSlug: tenant.value.slug,
                    tenantName: tenant.value.name
                })
            } catch (error) {
                logger.warn('AUTH_STORE', '⚠️ Failed to parse saved tenant context')
            }
        }

        if (savedToken && savedUser) {
            try {
                token.value = savedToken
                refreshToken.value = savedRefreshToken
                user.value = JSON.parse(savedUser)
                api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`

                logger.info('AUTH_STORE', '✅ Auth state restored from localStorage', {
                    userId: user.value.id,
                    userName: user.value.name || user.value.first_name,
                    userRole: user.value.role,
                    tokenLength: savedToken.length
                })

                // Verifica se il token è ancora valido (solo se c'è un token salvato)
                try {
                    await api.get('/auth/profile')
                    logger.info('AUTH_STORE', '✅ Token still valid')
                } catch (error) {
                    logger.warn('AUTH_STORE', '⚠️ Token expired, logging out')
                    logout()
                }
            } catch (error) {
                logger.error('AUTH_STORE', '❌ Failed to restore auth state', { error: error.message })
                logout()
            }
        } else {
            // Nessuna autenticazione salvata = utente ospite
            logger.debug('AUTH_STORE', '👤 No saved auth data - user is guest')
        }
    }

    // Funzione per login rapido admin (solo per sviluppo/admin)
    const quickAdminLogin = async () => {
        logger.info('AUTH_STORE', '🔐 Quick admin login initiated')
        return await autoLoginAdmin()
    }

    // Inizializza SENZA auto-login
    logger.debug('AUTH_STORE', '🏗️ Creating auth store instance')
    initAuth()

    return {
        // State
        user,
        token,
        refreshToken,
        tenant,
        isLoading,

        // Getters
        isAuthenticated,
        isAdmin,
        isSuperAdmin,
        userName,
        userRole,
        tenantId,
        tenantSlug,
        tenantName,

        // Actions
        login,
        register,
        logout,
        initAuth,
        quickAdminLogin,
        setTenantContext,
        validateTenantAccess
    }
})
